/**
 * Frontend Metrics Service
 *
 * This service collects and sends metrics to the backend for Prometheus monitoring.
 * Works with both Web and React Native (mobile) applications.
 */

type Platform = 'web' | 'mobile';

interface MetricsConfig {
  /** Base URL for the metrics API (e.g., https://api.fiveirongolf.com) */
  baseUrl: string;
  /** Platform identifier */
  platform: Platform;
  /** Environment (development, staging, production) */
  environment: string;
  /** Region (us, ae, au) */
  region: string;
  /** Enable/disable metrics collection */
  enabled: boolean;
  /** Authentication token for metrics endpoints (METRICS_AUTH_TOKEN) */
  authToken?: string;
  /** Batch interval in milliseconds (default: 10000) */
  batchInterval?: number;
  /** Maximum batch size before auto-flush (default: 50) */
  maxBatchSize?: number;
  /** Enable console logging for debugging */
  debug?: boolean;
}

interface PageViewEvent {
  page: string;
}

interface PageLoadEvent {
  page: string;
  duration: number;
}

interface InteractionEvent {
  action: string;
  component: string;
}

interface BookingFunnelEvent {
  step: string;
  status: 'started' | 'completed' | 'abandoned';
}

interface ErrorEvent {
  errorType: string;
  page: string;
  message?: string;
}

interface BatchPayload {
  pageViews?: Array<PageViewEvent & { environment: string; region: string; platform: Platform }>;
  pageLoads?: Array<PageLoadEvent & { environment: string; region: string; platform: Platform }>;
  interactions?: Array<InteractionEvent & { environment: string; region: string; platform: Platform }>;
  bookingFunnel?: Array<BookingFunnelEvent & { environment: string; region: string; platform: Platform }>;
  errors?: Array<ErrorEvent & { environment: string; region: string; platform: Platform }>;
}

class MetricsService {
  private config: MetricsConfig | null = null;
  private batch: BatchPayload = {};
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private isInitialized = false;

  /**
   * Initialize the metrics service with configuration
   */
  initialize(config: MetricsConfig): void {
    this.config = {
      batchInterval: 10000,
      maxBatchSize: 50,
      debug: false,
      ...config,
    };
    this.isInitialized = true;
    this.startBatchTimer();
    this.log('Metrics service initialized', config);
  }

  /**
   * Check if service is ready
   */
  isReady(): boolean {
    return this.isInitialized && this.config !== null && this.config.enabled;
  }

  /**
   * Record a page view
   */
  trackPageView(page: string): void {
    if (!this.isReady()) return;

    const event = {
      page,
      environment: this.config!.environment,
      region: this.config!.region,
      platform: this.config!.platform,
    };

    if (!this.batch.pageViews) this.batch.pageViews = [];
    this.batch.pageViews.push(event);
    this.log('Page view tracked', event);
    this.checkBatchSize();
  }

  /**
   * Record page load duration
   * @param page - Page identifier
   * @param duration - Load duration in seconds
   */
  trackPageLoad(page: string, duration: number): void {
    if (!this.isReady()) return;

    const event = {
      page,
      duration,
      environment: this.config!.environment,
      region: this.config!.region,
      platform: this.config!.platform,
    };

    if (!this.batch.pageLoads) this.batch.pageLoads = [];
    this.batch.pageLoads.push(event);
    this.log('Page load tracked', event);
    this.checkBatchSize();
  }

  /**
   * Record a user interaction
   */
  trackInteraction(action: string, component: string): void {
    if (!this.isReady()) return;

    const event = {
      action,
      component,
      environment: this.config!.environment,
      region: this.config!.region,
      platform: this.config!.platform,
    };

    if (!this.batch.interactions) this.batch.interactions = [];
    this.batch.interactions.push(event);
    this.log('Interaction tracked', event);
    this.checkBatchSize();
  }

  /**
   * Record a booking funnel event
   */
  trackBookingFunnel(step: string, status: 'started' | 'completed' | 'abandoned'): void {
    if (!this.isReady()) return;

    const event = {
      step,
      status,
      environment: this.config!.environment,
      region: this.config!.region,
      platform: this.config!.platform,
    };

    if (!this.batch.bookingFunnel) this.batch.bookingFunnel = [];
    this.batch.bookingFunnel.push(event);
    this.log('Booking funnel event tracked', event);
    this.checkBatchSize();
  }

  /**
   * Record a frontend error
   */
  trackError(errorType: string, page: string, message?: string): void {
    if (!this.isReady()) return;

    const event = {
      errorType,
      page,
      message,
      environment: this.config!.environment,
      region: this.config!.region,
      platform: this.config!.platform,
    };

    if (!this.batch.errors) this.batch.errors = [];
    this.batch.errors.push(event);
    this.log('Error tracked', event);
    // Errors should be sent immediately
    this.flush();
  }

  /**
   * Flush the current batch to the server
   */
  async flush(): Promise<void> {
    if (!this.isReady() || this.isBatchEmpty()) return;

    const payload = { ...this.batch };
    this.batch = {};

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add auth token if configured
      if (this.config!.authToken) {
        headers['Authorization'] = `Bearer ${this.config!.authToken}`;
      }

      const response = await fetch(`${this.config!.baseUrl}/metrics/frontend/batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      this.log('Batch sent successfully', payload);
    } catch (error) {
      this.log('Failed to send batch', { error, payload });
      // Re-add events to batch for retry
      this.mergeBatch(payload);
    }
  }

  /**
   * Stop the metrics service and flush remaining events
   */
  async shutdown(): Promise<void> {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
    await this.flush();
    this.isInitialized = false;
    this.log('Metrics service shutdown');
  }

  private startBatchTimer(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }
    this.batchTimer = setInterval(() => {
      this.flush();
    }, this.config!.batchInterval);
  }

  private checkBatchSize(): void {
    const totalSize =
      (this.batch.pageViews?.length || 0) +
      (this.batch.pageLoads?.length || 0) +
      (this.batch.interactions?.length || 0) +
      (this.batch.bookingFunnel?.length || 0) +
      (this.batch.errors?.length || 0);

    if (totalSize >= this.config!.maxBatchSize!) {
      this.flush();
    }
  }

  private isBatchEmpty(): boolean {
    return (
      !this.batch.pageViews?.length &&
      !this.batch.pageLoads?.length &&
      !this.batch.interactions?.length &&
      !this.batch.bookingFunnel?.length &&
      !this.batch.errors?.length
    );
  }

  private mergeBatch(payload: BatchPayload): void {
    if (payload.pageViews) {
      this.batch.pageViews = [...(this.batch.pageViews || []), ...payload.pageViews];
    }
    if (payload.pageLoads) {
      this.batch.pageLoads = [...(this.batch.pageLoads || []), ...payload.pageLoads];
    }
    if (payload.interactions) {
      this.batch.interactions = [...(this.batch.interactions || []), ...payload.interactions];
    }
    if (payload.bookingFunnel) {
      this.batch.bookingFunnel = [...(this.batch.bookingFunnel || []), ...payload.bookingFunnel];
    }
    if (payload.errors) {
      this.batch.errors = [...(this.batch.errors || []), ...payload.errors];
    }
  }

  private log(message: string, data?: unknown): void {
    if (this.config?.debug) {
      console.log(`[Metrics] ${message}`, data || '');
    }
  }
}

// Export singleton instance
export const metricsService = new MetricsService();

// Export types for consumers
export type { MetricsConfig, Platform, PageViewEvent, PageLoadEvent, InteractionEvent, BookingFunnelEvent, ErrorEvent };

